import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CanActivateChildFn,
  MaybeAsync,
  RedirectFunction,
  Router,
  UrlTree,
} from '@angular/router';
import { OAuthService } from '@bp-core/src/lib/external/angular-oauth2-oidc-jwks/oauth-service';
import { bpNavigation } from 'app/mock-api/common/navigation/data';
import { homeFor, isUrlAllowed } from 'bp/shared/access/sudo-navigation.filter';
import { SudoAccessStore } from 'bp/shared/services/sudo-access.store';
import { map, take } from 'rxjs';

/** Página «sin módulos» (feature 057, artboard 2.a). */
export const NO_MODULES_URL = '/sin-modulos';

const UNAUTHORIZED_URL = '/errors/401';

/**
 * Página en la que aterriza quien no puede abrir la que pidió: su inicio (`homeFor`) si tiene acceso al
 * sudo, o `/errors/401` si no lo tiene. Con los permisos ya cargados; los guards legados la usan cuando
 * `sudoAccessGuard` no dejó la navegación pendiente (`SudoAccessDeferral`).
 */
export function landingUrl(store: SudoAccessStore): string {
  return store.hasSudoAccess() ? homeFor(store.ctx(), bpNavigation) : UNAUTHORIZED_URL;
}

/** Destino de siempre: sin sesión o sin permisos cargados, el shell decide con todo el contexto. */
const LEGACY_HOME_URL = '/dashboard';

const DENIED_MESSAGE = 'Esa página no está disponible para tu cuenta.';
const DENIED_ACTION = 'Cerrar';
const DENIED_DURATION_MS = 4000;

/**
 * Avisa «Esa página no está disponible para tu cuenta.» una sola vez por navegación: el guard del shell
 * corre una vez por cada nivel de ruta hija, y sin esto el aviso se apilaría.
 */
@Injectable({ providedIn: 'root' })
class SudoDeniedNotifier {
  private readonly snackBar = inject(MatSnackBar);
  private lastNavigationId: number | null = null;

  notify(navigationId: number | null): void {
    if (navigationId !== null && navigationId === this.lastNavigationId) return;
    this.lastNavigationId = navigationId;
    this.snackBar.open(DENIED_MESSAGE, DENIED_ACTION, { duration: DENIED_DURATION_MS });
  }
}

/**
 * Recuerda la navegación que `sudoAccessGuard` dejó pasar sin decidir porque los permisos aún no
 * llegaban (carga directa de una URL: recarga o enlace pegado). Los guards legados de cada ruta
 * (`AuthGuard`, `IaManagerGuard`, `userNotificationsGuard`, `sudoGuard`) deciden solo con los roles del
 * token y mandan a `/errors/401` a quien tiene un rol del sudo que ellos no conocen (p. ej. `marketing`).
 * En esa navegación cedan el paso: el layout retiene el contenido, espera los permisos y revalida la URL
 * (`revalidateSudoAccess`), que lleva a la persona a su inicio con el aviso de siempre (H4).
 *
 * Se identifica por id de navegación, así que una redirección posterior (otra navegación) vuelve a
 * pasar por los guards con normalidad.
 */
@Injectable({ providedIn: 'root' })
export class SudoAccessDeferral {
  private readonly router = inject(Router);
  private deferredNavigationId: number | null = null;

  /** Marca la navegación en curso como pendiente de los permisos. */
  defer(): void {
    this.deferredNavigationId = this.router.getCurrentNavigation()?.id ?? null;
  }

  /** `true` si la navegación en curso quedó pendiente de los permisos: el guard legado no debe decidir. */
  isDeferred(): boolean {
    const id = this.router.getCurrentNavigation()?.id ?? null;
    return id !== null && id === this.deferredNavigationId;
  }
}

/** Atajo para los guards funcionales legados (contexto de inyección). */
export function isSudoAccessDeferred(): boolean {
  return inject(SudoAccessDeferral).isDeferred();
}

interface Redirection {
  tree: UrlTree;
  /** `true` si la persona intentó abrir una página que no tiene y se le avisa. */
  notify: boolean;
}

/**
 * Decide si se puede abrir `url`. `null` = se deja pasar. Mientras los permisos cargan (`loading`) o
 * fallaron (`error`) se deja pasar: el layout retiene el contenido o muestra el reintento, y revalida
 * la URL al quedar `ready`.
 */
function decide(url: string, store: SudoAccessStore, router: Router): Redirection | null {
  if (store.status() !== 'ready') return null;

  const ctx = store.ctx();
  // Sin rol del sudo no hay acceso, tenga o no la marca `isAdmin` (BT-7551, D-3).
  if (!store.hasSudoAccess()) {
    return { tree: router.parseUrl(UNAUTHORIZED_URL), notify: false };
  }

  if (url.split(/[?#]/)[0] === NO_MODULES_URL) {
    const home = homeFor(ctx, bpNavigation);
    return home === NO_MODULES_URL ? null : { tree: router.parseUrl(home), notify: false };
  }

  if (isUrlAllowed(url, ctx, bpNavigation)) return null;
  return { tree: router.parseUrl(homeFor(ctx, bpNavigation)), notify: true };
}

/**
 * Guard de shell (`canActivateChild` de la ruta autenticada de `app.routes.ts`): reemplaza a
 * `restrictedRoleGuard` y ya no confina solo a Customer Success, sino a cualquiera según su menú
 * (contrato §3, opción B de R-12). Varias rutas del sudo no tienen guard propio: este es el control.
 */
export const sudoAccessGuard: CanActivateChildFn = (_route, state) => {
  const store = inject(SudoAccessStore);
  const router = inject(Router);
  const notifier = inject(SudoDeniedNotifier);
  const deferral = inject(SudoAccessDeferral);

  // En paralelo con `GetMe`: la primera vez arranca la carga; después no hace nada.
  store.load();

  // Sin permisos todavía: se deja pasar y se avisa a los guards legados de la ruta (ver `SudoAccessDeferral`).
  if (store.status() !== 'ready') deferral.defer();

  const redirection = decide(state.url, store, router);
  if (!redirection) return true;
  if (redirection.notify) notifier.notify(router.getCurrentNavigation()?.id ?? null);
  return redirection.tree;
};

/**
 * Revalida la URL actual cuando el store pasa a `ready` (la llama el layout dentro de un contexto de
 * inyección): la primera navegación se dejó pasar mientras cargaban los permisos. Resuelve cuando
 * terminó de navegar (o enseguida, si la URL está permitida).
 */
export async function revalidateSudoAccess(): Promise<void> {
  const store = inject(SudoAccessStore);
  const router = inject(Router);
  const notifier = inject(SudoDeniedNotifier);

  const redirection = decide(router.url, store, router);
  if (!redirection) return;
  // No es una navegación en curso: no hay id con el cual deduplicar.
  if (redirection.notify) notifier.notify(null);
  await router.navigateByUrl(redirection.tree);
}

/**
 * `redirectTo` de la raíz `''`: espera los permisos y aterriza en la página de inicio de la persona,
 * sin snackbar (FR-047, FR-052). Sin sesión, o si los permisos no cargan o no hay acceso al sudo, sigue
 * a `/dashboard` como siempre: ahí el login o la 401 deciden con el usuario ya cargado.
 */
export const sudoRootRedirect: RedirectFunction = (): MaybeAsync<string> => {
  const store = inject(SudoAccessStore);
  const oauthService = inject(OAuthService);

  if (!oauthService.hasValidAccessToken()) return LEGACY_HOME_URL;

  return store.load().pipe(
    take(1),
    map(() => {
      const ctx = store.ctx();
      if (store.status() !== 'ready' || !store.hasSudoAccess()) {
        return LEGACY_HOME_URL;
      }
      return homeFor(ctx, bpNavigation);
    }),
  );
};
